import { LearningGoal } from '@/domain/models/onboarding/learning-goal'
import { PreferredContentFormat } from '@/domain/models/onboarding/preferred-content-format'

/** ==================== Vocabulário ==================== */

/**
 * Preferência do usuário por tipo de aula.
 *
 * Não confundir com `ClassType` (domain/models/content/class-type.ts), que descreve
 * a OFERTA no catálogo e espelha o `ClassTypeEnum` do backend (INDIVIDUAL_GROUP,
 * PLATFORM, ids numéricos). Aqui o domínio é a PREFERÊNCIA: `ANY` significa
 * "tanto faz", não "individual e em grupo ao mesmo tempo".
 *
 * Enum puro de propósito: rótulo traduzido e ícone são preocupação de apresentação
 * e vivem em `src/presentation/helpers/`, não aqui — a camada data carrega só o
 * vocabulário do contrato.
 */
export enum PreferredClassType {
  INDIVIDUAL = 'INDIVIDUAL',
  GROUP = 'GROUP',
  ANY = 'ANY'
}

/** ==================== API ==================== */

/**
 * Shape cru devolvido pelo backend.
 *
 * O `axios-http-client` camelizza toda resposta JSON no interceptor, então o
 * backend pode responder em snake_case (`weekly_dedication`) que aqui
 * chega camelCase. Nada a configurar.
 *
 * `null` em qualquer campo significa "o usuário ainda não preencheu" — é o que
 * habilita o estado vazio dos chips no Meu Espaço.
 */
export type LearningPreferencesApiData = {
  learningGoal: string | null
  weeklyDedication: number | null
  preferredClassType: string | null
  contentFormats: string[] | null
}

/** ==================== Domínio ==================== */

export type LearningPreferencesModel = {
  learningGoal: LearningGoal.Enum | null
  /**
   * HORAS por semana (1 a 20 — ver `WEEKLY_DEDICATION`).
   *
   * O nome não carrega a unidade porque espelha o contrato (`weekly_dedication`). Não confundir
   * com o `weeklyDedication` legado do onboarding (`UserSpaceLoaderDataType`), que é um enum em
   * DIAS: nome idêntico, unidade diferente, e os dois chegam ao Meu Espaço.
   */
  weeklyDedication: number | null
  preferredClassType: PreferredClassType | null
  contentFormats: PreferredContentFormat.Enum[]
}

export const EMPTY_LEARNING_PREFERENCES: LearningPreferencesModel = {
  learningGoal: null,
  weeklyDedication: null,
  preferredClassType: null,
  contentFormats: []
}

/** ==================== Escrita ==================== */

/**
 * Payload PARCIAL do PATCH. A distinção entre os três estados é significativa e
 * precisa sobreviver até o corpo da requisição:
 *
 * - chave ausente  → não altera o campo
 * - `null`         → limpa o campo
 * - `[]`           → limpa a lista de formatos
 *
 * `buildLearningPreferencesPatchBody` é quem preserva essa distinção.
 *
 * As chaves aqui definem as chaves no fio: o `axios-http-client` decameliza o corpo, então
 * `weeklyDedication` sai como `weekly_dedication`. Renomear um campo deste tipo muda o contrato.
 */
export type LearningPreferencesPatchParams = {
  learningGoal?: string | null
  weeklyDedication?: number | null
  preferredClassType?: string | null
  contentFormats?: string[] | null
}

/** Chave da query no React Query — compartilhada entre o loader e o invalidate do save. */
export const LEARNING_PREFERENCES_QUERY_KEY = 'user-learning-preferences'

/** Limites do slider de disponibilidade, em HORAS por semana. */
export const WEEKLY_DEDICATION = {
  MIN: 1,
  MAX: 20
} as const

export const MAX_CONTENT_FORMATS = 2
